import { Badge } from '@/components/ui/badge'
import { InquiryTagEnum } from '@/gen/cortado/v1/settings_pb'
import { lastContactLine, quietForDays, type MailSignalFields } from '@/lib/mail-signals'
import { cn } from '@/lib/utils'

// Waiting and Reply needed follow the lead's mail (CPRO-790): the newest
// message sets one or the other. They render as words, not icons, so whose
// move it is reads at a glance.

const MAIL_TAGS = new Set<InquiryTagEnum>([
  InquiryTagEnum.INQUIRY_TAG_WAITING,
  InquiryTagEnum.INQUIRY_TAG_REPLY_NEEDED,
])

/** The tags other than Waiting / Reply needed, for the plain tag list. */
export function withoutMailTags(tags?: InquiryTagEnum[]): InquiryTagEnum[] {
  return (tags ?? []).filter(t => t !== InquiryTagEnum.INQUIRY_TAG_UNKNOWN && !MAIL_TAGS.has(t))
}

const AUTO_HINT = 'Updates on its own when email comes in or goes out. Change it by hand and it holds until the next email.'

interface LeadMailBadgeProps extends MailSignalFields {
  tags?: InquiryTagEnum[]
  className?: string
  now?: Date
}

/**
 * "Reply needed" in red when the customer wrote last, "Waiting 5d" in grey
 * when we did. Nothing when the lead has neither tag.
 */
export function LeadMailBadge({ tags, className, now, ...mail }: LeadMailBadgeProps) {
  if (tags?.includes(InquiryTagEnum.INQUIRY_TAG_REPLY_NEEDED)) {
    return (
      <Badge variant="danger" className={className} title={AUTO_HINT}>
        Reply needed
      </Badge>
    )
  }
  if (tags?.includes(InquiryTagEnum.INQUIRY_TAG_WAITING)) {
    const days = quietForDays(mail, now)
    return (
      <Badge variant="secondary" className={className} title={AUTO_HINT}>
        {days !== undefined && days > 0 ? `Waiting ${days}d` : 'Waiting'}
      </Badge>
    )
  }
  return null
}

interface LeadLastContactProps extends MailSignalFields {
  tags?: InquiryTagEnum[]
  contactFirstName?: string
  className?: string
}

/** The lead page line: the mail badge and "Last heard from Jane 3 days ago". */
export function LeadLastContact({ tags, contactFirstName, className, ...mail }: LeadLastContactProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2 text-sm text-muted-foreground', className)}>
      <LeadMailBadge tags={tags} {...mail} />
      <span>{lastContactLine(mail, contactFirstName)}</span>
    </div>
  )
}
